import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QIconButton, QTooltip } from '../../QAtoms';
import { QBox, QFlex } from '../';

type BackToTopWrapperProps = {
  children: React.ReactNode;
  parentRef: React.RefObject<HTMLElement>;
};

const BackToTopWrapper: React.FC<BackToTopWrapperProps> = ({
  children,
  parentRef,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isFixed, setIsFixed] = useState(true);
  const [, setForceRender] = useState(0);

  const lastParentOffsetRef = useRef<number | null>(null);

  useEffect(() => {
    if (!parentRef.current) {
      return;
    }

    const parent = parentRef.current;

    const getOffsetTop = (element: HTMLElement): number => {
      let offset = element.offsetTop;
      if (element.offsetParent instanceof HTMLElement) {
        offset += getOffsetTop(element.offsetParent);
      }
      return offset;
    };

    const getCurrentOffset = (): number => {
      const currentOffset = getOffsetTop(parent);

      if (currentOffset > 0) {
        lastParentOffsetRef.current = currentOffset;
        return currentOffset;
      }

      return lastParentOffsetRef.current ?? 0;
    };

    const initialParentOffset = getCurrentOffset();

    if (initialParentOffset === 0) {
      const handle = setTimeout(() => setForceRender((n) => n + 1), 500);
      return () => clearTimeout(handle);
    }

    const BOTTOM_OFFSET_PX = 2 * 4;

    const checkPosition = () => {
      const parentOffset = getCurrentOffset();
      const parentOffsetBottom = parentOffset + parent.offsetHeight;

      const viewportHeight = window.innerHeight;
      const fixedButtonBottom =
        window.scrollY + viewportHeight - BOTTOM_OFFSET_PX;

      setIsFixed(fixedButtonBottom < parentOffsetBottom);
    };

    window.addEventListener('scroll', checkPosition);
    window.addEventListener('resize', checkPosition);

    checkPosition();

    return () => {
      window.removeEventListener('scroll', checkPosition);
      window.removeEventListener('resize', checkPosition);
    };
  }, [wrapperRef, parentRef]);

  if (!parentRef.current) {
    return null;
  }

  return (
    <QFlex
      ref={wrapperRef}
      position={isFixed ? 'fixed' : 'relative'}
      bottom={isFixed ? '6' : '0'}
    >
      {children}
    </QFlex>
  );
};

type BackToTopProps = {
  parentRef: React.RefObject<HTMLElement>;
};

export const BackToTop: React.FC<BackToTopProps> = ({ parentRef }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  return (
    <BackToTopWrapper parentRef={parentRef}>
      <QBox
        flexGrow={0}
        opacity={isVisible ? '1' : '0'}
        visibility={isVisible ? 'visible' : 'hidden'}
        transition="all 0.3s ease-in-out"
        zIndex="10"
        data-cy="back-to-top-button"
        data-testid="back-to-top-button"
      >
        <QTooltip label="Back to top" placement="top">
          <QBox>
            <QIconButton
              variant="outline"
              onClick={scrollToTop}
              aria-label="Back to top"
              iconName="ArrowUp"
              size="sm"
            />
          </QBox>
        </QTooltip>
      </QBox>
    </BackToTopWrapper>
  );
};
